import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Target, Award, BarChart3, RefreshCcw, Info, Globe2, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import AccuracyStreaksStrip from "../components/AccuracyStreaksStrip";
import { useAuth } from "../context/AuthContext";

const accuracyTone = (a) =>
  a >= 75 ? "text-success" : a >= 60 ? "text-warning" : "text-destructive";

const accuracyBar = (a) =>
  a >= 75 ? "bg-success" : a >= 60 ? "bg-warning" : "bg-destructive";

const StatTile = ({ label, value, Icon, testid, tone }) => (
  <div className="nd-card p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="eyebrow">{label}</p>
        <p className={`mt-2 font-display font-bold text-3xl sm:text-4xl font-tabular ${tone || "text-foreground"}`}>
          {value}
        </p>
      </div>
      <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center">
        <Icon className="w-4.5 h-4.5 text-foreground" />
      </div>
    </div>
  </div>
);

const AccuracyPage = () => {
  const { user, token } = useAuth();
  const isAdmin = !!(user && user.is_admin);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);
  const [settleResult, setSettleResult] = useState(null);
  const [cron, setCron] = useState(null);
  const [history, setHistory] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [calibrationLeague, setCalibrationLeague] = useState("_GLOBAL_");
  const [byLeague, setByLeague] = useState(null);
  const [byLeagueDays, setByLeagueDays] = useState(30);
  // Iter 145h — lowered floor from 10 to 5 so smaller leagues stop
  // disappearing from the breakdown.  Exposed as a UI selector below.
  const [byLeagueMinSamples, setByLeagueMinSamples] = useState(5);
  const [expandedLeague, setExpandedLeague] = useState(null);
  // Iter 145bi — Engine toggle.  Quantum reads from /api/quantum/*
  // endpoints which mirror the classic shape.
  const [engine, setEngine] = useState("classic");
  // Iter 145bk — Admin can hide leagues from the by-league stats.
  const [showHidden, setShowHidden] = useState(false);
  const accuracyBase = engine === "quantum" ? "/quantum/accuracy" : "/accuracy";

  const hideLeague = async (L) => {
    if (!isAdmin) return;
    try {
      await axios.post(`${API}/admin/accuracy/league/hide`,
        { league_name: L.league_name, country: L.country || "" },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Hidden: ${L.league_name}`);
      fetchByLeague(byLeagueDays, byLeagueMinSamples);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hide failed");
    }
  };

  const unhideLeague = async (L) => {
    if (!isAdmin) return;
    try {
      await axios.post(`${API}/admin/accuracy/league/unhide`,
        { league_name: L.league_name, country: L.country || "" },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Unhidden: ${L.league_name}`);
      fetchByLeague(byLeagueDays, byLeagueMinSamples);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unhide failed");
    }
  };

  useEffect(() => {
    setCalibrationLeague("_GLOBAL_");
    fetchAccuracyStats();
    fetchByLeague(byLeagueDays);
    // Iter 145bj — Calibration is now engine-aware (Quantum has its
    // own bucket table at /api/quantum/accuracy/calibration).
    fetchCalibration("_GLOBAL_");
    if (engine === "classic") {
      fetchCronStatus();
      fetchSettleHistory();
    }
  }, [engine]);

  // Iter 145bk — Refetch by-league when the show-hidden toggle flips.
  useEffect(() => {
    fetchByLeague(byLeagueDays, byLeagueMinSamples);
  }, [showHidden]);

  useEffect(() => {
    const tick = setInterval(() => {
      if (engine === "classic") {
        fetchCronStatus();
        fetchSettleHistory();
      }
    }, 60_000);
    return () => clearInterval(tick);
  }, [engine]);

  const fetchAccuracyStats = async () => {
    try {
      const response = await axios.get(`${API}${accuracyBase}/stats`);
      setStats(response.data);
      setLoading(false);
    } catch {
      toast.error("Failed to load accuracy statistics");
      setLoading(false);
    }
  };
  const fetchCronStatus = async () => { try { setCron((await axios.get(`${API}/cron/status`)).data); } catch {} };
  const fetchSettleHistory = async () => { try { setHistory((await axios.get(`${API}/accuracy/settle/history?limit=7`)).data); } catch {} };
  const fetchCalibration = async (league = "_GLOBAL_") => {
    const base = engine === "quantum" ? "/quantum/accuracy/calibration" : "/accuracy/calibration";
    try { setCalibration((await axios.get(`${API}${base}?league=${encodeURIComponent(league)}`)).data); } catch {}
  };
  const fetchByLeague = async (days = 30, samplesArg = byLeagueMinSamples) => {
    try {
      const r = await axios.get(`${API}${accuracyBase}/by_league`, {
        params: {
          days, min_samples: samplesArg, limit: 500,
          include_hidden: showHidden,
        },
      });
      setByLeague(r.data);
    } catch {}
  };
  const onByLeagueDaysChange = (d) => { setByLeagueDays(d); fetchByLeague(d, byLeagueMinSamples); };
  const onByLeagueMinSamplesChange = (s) => { setByLeagueMinSamples(s); fetchByLeague(byLeagueDays, s); };

  const onLeagueChange = (e) => { const lg = e.target.value; setCalibrationLeague(lg); fetchCalibration(lg); };

  const formatRelativeDay = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const today = new Date();
    const diffDays = Math.floor((today - d) / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatCountdown = (secs) => {
    if (secs == null) return "—";
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const settleJob = cron?.jobs?.find((j) => j.name?.includes("settle"));

  const handleSettleNow = async () => {
    setSettling(true); setSettleResult(null);
    try {
      const r = await axios.post(`${API}/accuracy/settle?sync=true&lookback_hours=72`, null, { timeout: 90000 });
      setSettleResult(r.data);
      if (r.data.markets_settled_this_run > 0) {
        toast.success(`Settled ${r.data.markets_settled_this_run} markets across ${r.data.fixtures_processed} fixtures`);
        await fetchAccuracyStats();
        await fetchCalibration(calibrationLeague);
      } else if (r.data.api_errors > 0) {
        toast.warning(`Couldn't reach data feed for ${r.data.api_errors} fixtures. Try again later.`);
      } else if (r.data.not_finished_yet > 0) {
        toast.info(`${r.data.not_finished_yet} predicted matches haven't finished yet.`);
      } else {
        toast.info("Nothing new to settle right now.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Settle failed — check backend logs");
    } finally { setSettling(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 nd-fade-up">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="eyebrow flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5" /> Model performance
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.03em] mt-2">
              Accuracy.
            </h1>
            {/* Iter 145bi — Engine switcher. */}
            <div
              className="mt-4 inline-flex items-center gap-0 rounded-full border border-border bg-card p-0.5"
              data-testid="accuracy-engine-switch"
            >
              {[
                { key: "classic", label: "Classic" },
                { key: "quantum", label: "Quantum" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setEngine(opt.key)}
                  data-testid={`accuracy-engine-${opt.key}`}
                  className={`px-4 py-1.5 text-xs font-bold uppercase tracking-[0.1em] rounded-full transition ${
                    engine === opt.key
                      ? (opt.key === "quantum"
                          ? "bg-cyan-500 text-cyan-950"
                          : "bg-foreground text-background")
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {settleJob && (
              <div
                className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-full border border-border bg-card"
                data-testid="cron-countdown"
                title={`Next auto-settle: ${settleJob.next_run_at}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                <span className="eyebrow">Auto-settle</span>
                <span className="font-tabular font-bold text-foreground text-xs">
                  {formatCountdown(settleJob.seconds_until_next_run)}
                </span>
              </div>
            )}
            <Button
              onClick={handleSettleNow}
              disabled={settling}
              className="h-9 rounded-full px-4 bg-foreground text-background hover:bg-foreground/90"
              data-testid="settle-now-btn"
            >
              <RefreshCcw className={`w-4 h-4 mr-1.5 ${settling ? "animate-spin" : ""}`} />
              {settling ? "Settling" : "Settle now"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Iter 145i — Hot/Cold streak highlight strip. */}
        <div className="mb-8" data-testid="accuracy-streaks-section">
          <AccuracyStreaksStrip />
        </div>

        {(stats?.total_predictions || 0) === 0 && (
          <div className="rounded-xl border border-border bg-subtle p-5 mb-8 flex items-start gap-3" data-testid="accuracy-empty-banner">
            <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong>Track record is still building.</strong>{" "}
              <span className="text-muted-foreground">
                Accuracy fills in after predicted matches finish and we settle them against the
                real result. Hit <em>Settle Now</em> any time, or wait for the daily auto-settle.
              </span>
              {settleResult && (
                <div className="mt-2 text-xs text-muted-foreground font-tabular" data-testid="settle-result-detail">
                  Last run · fixtures: {settleResult.fixture_ids_in_window} ·
                  {" "}processed: {settleResult.fixtures_processed} ·
                  {" "}settled: {settleResult.markets_settled_this_run} ·
                  {" "}pending: {settleResult.not_finished_yet} ·
                  {" "}errors: {settleResult.api_errors}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatTile label="Total predictions" value={stats?.total_predictions || 0} Icon={Target} testid="acc-total" />
          <StatTile label="Markets analysed" value={stats?.total_markets_predicted || 0} Icon={BarChart3} testid="acc-markets" />
          <StatTile label="Correct picks" value={stats?.correct_predictions || 0} Icon={Award} testid="acc-correct" tone="text-success" />
          <StatTile
            label="Overall accuracy"
            value={`${stats?.accuracy_rate || 0}%`}
            Icon={TrendingUp}
            testid="acc-overall"
            tone={accuracyTone(stats?.accuracy_rate || 0)}
          />
        </div>

        {history && (history.runs?.length > 0 || history.ready_to_grade > 0) && (
          <div className="nd-card p-5 mb-8" data-testid="settle-history-block">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="eyebrow">Auto-settle activity</span>
              </div>
              {history.ready_to_grade > 0 && (
                <Badge className="bg-warning/15 text-warning border border-warning/40">
                  {history.ready_to_grade} fixtures ready to grade
                </Badge>
              )}
            </div>
            <div className="space-y-1.5" data-testid="settle-history-list">
              {(history.runs || []).slice(0, 5).map((run, i) => (
                <div key={i} className="flex items-center justify-between text-xs font-tabular">
                  <span className="text-muted-foreground w-24">{formatRelativeDay(run.run_at)}</span>
                  <span className="flex-1">
                    <span className="text-success font-bold">{run.markets_settled_this_run || 0}</span>
                    <span className="text-muted-foreground"> markets settled</span>
                    {run.fixtures_processed > 0 && (
                      <span className="text-muted-foreground"> · {run.fixtures_processed} fixtures</span>
                    )}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(run.run_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              ))}
              {(!history.runs || history.runs.length === 0) && (
                <p className="text-xs text-muted-foreground">No settle runs yet.</p>
              )}
            </div>
          </div>
        )}

        {calibration && calibration.row_count > 0 && (
          <div className="nd-card p-5 mb-8" data-testid="calibration-block">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                <span className="eyebrow">Self-learning calibration</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={calibrationLeague}
                  onChange={onLeagueChange}
                  data-testid="calibration-league-select"
                  className="bg-card border border-border text-foreground text-xs rounded-full h-8 px-3 max-w-[220px]"
                >
                  <option value="_GLOBAL_">All leagues (global)</option>
                  {(calibration?.available_leagues || []).map((lg) => (
                    <option key={lg} value={lg}>{lg}</option>
                  ))}
                </select>
                <Badge className="bg-muted text-foreground border border-border">
                  {calibration.row_count} buckets
                </Badge>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {calibrationLeague === "_GLOBAL_"
                ? <>Showing <strong>global</strong> calibration. Pick a league above for league-specific tuning.</>
                : <>League-specific data for <strong>{calibrationLeague}</strong>.</>}
            </p>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="calibration-table">
              {Object.entries(calibration.by_market).map(([market, rows]) => (
                <div key={market}>
                  <div className="eyebrow mb-1.5">{market}</div>
                  <div className="space-y-1">
                    {rows.map((r, i) => {
                      const trusted = r.samples >= 25;
                      const overconf = r.delta_pp < -5;
                      return (
                        <div key={i} className="grid grid-cols-12 gap-2 text-xs font-tabular items-center px-2 py-1.5 rounded-lg bg-subtle border border-border">
                          <span className="col-span-2 text-muted-foreground">{r.bucket_low}-{r.bucket_high}%</span>
                          <span className="col-span-2">
                            n={r.samples}{trusted && <span className="text-success ml-1">●</span>}
                          </span>
                          <span className="col-span-2 text-muted-foreground">said {r.raw_avg_conf}%</span>
                          <span className="col-span-3">actual <span className="font-bold">{r.actual_hit_rate}%</span></span>
                          <span className={`col-span-3 text-right font-semibold ${
                            r.delta_pp >= 0 ? "text-success" : overconf ? "text-destructive" : "text-warning"
                          }`}>
                            {r.delta_pp >= 0 ? "+" : ""}{r.delta_pp}pp
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <ValueMarketsAccuracyTile />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="nd-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-4 h-4" />
              <h2 className="font-display text-lg font-semibold">Accuracy by market</h2>
            </div>
            <div className="space-y-4">
              {stats?.by_market && Object.keys(stats.by_market).length > 0 ? (
                Object.entries(stats.by_market).map(([market, data]) => (
                  <div key={market} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{market}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground font-tabular">{data.correct}/{data.total}</span>
                        <span className={`text-sm font-bold font-tabular ${accuracyTone(data.accuracy)}`}>{data.accuracy}%</span>
                      </div>
                    </div>
                    <div className="confidence-track">
                      <div className={`h-full rounded-full ${accuracyBar(data.accuracy)}`} style={{ width: `${data.accuracy}%` }} />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm text-center py-4">No market data yet</p>
              )}
            </div>
          </div>

          <div className="nd-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-4 h-4" />
              <h2 className="font-display text-lg font-semibold">Accuracy by risk level</h2>
            </div>
            <div className="space-y-5">
              {stats?.by_risk_level && Object.keys(stats.by_risk_level).length > 0 ? (
                Object.entries(stats.by_risk_level).map(([risk, data]) => (
                  <div key={risk} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge
                        className={`${
                          risk === "Low" ? "bg-success/10 text-success border-success/30"
                          : risk === "Medium" ? "bg-warning/10 text-warning border-warning/30"
                          : "bg-destructive/10 text-destructive border-destructive/30"
                        } border`}
                      >
                        {risk} risk
                      </Badge>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground font-tabular">{data.correct}/{data.total}</span>
                        <span className={`text-lg font-bold font-tabular ${accuracyTone(data.accuracy)}`}>{data.accuracy}%</span>
                      </div>
                    </div>
                    <div className="confidence-track h-2.5">
                      <div className={`h-full rounded-full ${accuracyBar(data.accuracy)}`} style={{ width: `${data.accuracy}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {risk === "Low" && "Conservative picks with highest win probability"}
                      {risk === "Medium" && "Balanced risk-reward betting opportunities"}
                      {risk === "High" && "Higher risk selections with potential big returns"}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm text-center py-4">No risk level data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Accuracy by league — sorted high → low, supports 7d / 30d / 90d / 180d windows */}
        <div className="nd-card p-6" data-testid="accuracy-by-league">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Globe2 className="w-4 h-4" />
              <h2 className="font-display text-lg font-semibold">Accuracy by league</h2>
              {byLeague && (
                <span className="text-xs text-muted-foreground font-tabular">
                  · {byLeague.league_count} leagues, ≥{byLeagueMinSamples} settled markets
                  {byLeague.hidden_count > 0 && ` · ${byLeague.hidden_count} hidden`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Iter 145bk — Admin-only: toggle hidden leagues into view. */}
              {isAdmin && (byLeague?.hidden_count ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setShowHidden((v) => !v)}
                  data-testid="toggle-show-hidden-leagues"
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1 ${
                    showHidden
                      ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {showHidden ? "Hiding off" : "Show hidden"}
                </button>
              )}
              <div className="flex items-center gap-1" data-testid="league-window-tabs">
                {[7, 30, 90, 180, 0].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => onByLeagueDaysChange(d)}
                    className={`text-xs font-bold px-2.5 py-1 rounded border transition-colors ${
                      byLeagueDays === d
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`league-window-${d === 0 ? "all" : `${d}d`}`}
                  >
                    {d === 0 ? "All" : `${d}d`}
                  </button>
                ))}
              </div>
              {/* Iter 145h — min_samples selector so users can see
                 leagues that previously got filtered out at the
                 hard-coded 10-sample floor. */}
              <div className="flex items-center gap-1" data-testid="league-min-samples-tabs">
                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold mr-1">
                  min
                </span>
                {[1, 5, 10, 20, 50].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onByLeagueMinSamplesChange(s)}
                    className={`text-xs font-bold px-2 py-1 rounded border transition-colors ${
                      byLeagueMinSamples === s
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`league-min-samples-${s}`}
                  >
                    ≥{s}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {byLeague?.leagues?.length ? (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm" data-testid="league-table">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left font-semibold py-2 pr-3">League</th>
                    <th className="text-left font-semibold py-2 pr-3 hidden sm:table-cell">Country</th>
                    <th className="text-right font-semibold py-2 pr-3">Settled</th>
                    <th className="text-right font-semibold py-2 pr-3">Hits</th>
                    <th className="text-right font-semibold py-2 pl-3">Accuracy</th>
                    {isAdmin && <th className="text-right font-semibold py-2 pl-3 w-12">·</th>}
                  </tr>
                </thead>
                <tbody>
                  {byLeague.leagues.map((L) => {
                    const slug = L.league_name.toLowerCase().replace(/\s+/g, "-");
                    const isOpen = expandedLeague === `${L.league_name}|${L.country}`;
                    const markets = L.by_market
                      ? Object.entries(L.by_market).sort((a, b) => b[1].accuracy_pct - a[1].accuracy_pct)
                      : [];
                    return (
                      <React.Fragment key={`${L.league_name}-${L.country}`}>
                        <tr
                          onClick={() =>
                            setExpandedLeague(isOpen ? null : `${L.league_name}|${L.country}`)
                          }
                          className={`border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer ${
                            L.hidden ? "opacity-50" : ""
                          }`}
                          data-testid={`league-row-${slug}`}
                        >
                          <td className="py-2.5 pr-3 font-medium flex items-center gap-1.5">
                            <span className={`text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}>›</span>
                            {L.league_name}
                            {L.hidden && (
                              <span
                                className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-amber-300/80 border border-amber-500/30 bg-amber-500/10 rounded px-1.5 py-0.5"
                              >
                                hidden
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 pr-3 text-muted-foreground hidden sm:table-cell">{L.country || "—"}</td>
                          <td className="py-2.5 pr-3 text-right font-tabular text-muted-foreground">{L.total}</td>
                          <td className="py-2.5 pr-3 text-right font-tabular text-muted-foreground">{L.correct}</td>
                          <td className="py-2.5 pl-3 text-right">
                            <span className={`font-bold font-tabular ${accuracyTone(L.accuracy_pct)}`}>{L.accuracy_pct.toFixed(1)}%</span>
                          </td>
                          {/* Iter 145bk — Admin-only hide / unhide action */}
                          {isAdmin && (
                            <td className="py-2.5 pl-3 text-right">
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  if (L.hidden) unhideLeague(L);
                                  else hideLeague(L);
                                }}
                                data-testid={`league-${L.hidden ? "unhide" : "hide"}-${slug}`}
                                title={L.hidden ? "Restore this league to stats" : "Exclude this league from stats"}
                                className={`p-1.5 rounded-full transition-colors ${
                                  L.hidden
                                    ? "text-emerald-400 hover:bg-emerald-500/10"
                                    : "text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10"
                                }`}
                              >
                                {L.hidden
                                  ? <Eye className="w-3.5 h-3.5" />
                                  : <EyeOff className="w-3.5 h-3.5" />}
                              </button>
                            </td>
                          )}
                        </tr>
                        {isOpen && markets.length > 0 && (
                          <tr className="bg-muted/20" data-testid={`league-row-${slug}-markets`}>
                            <td colSpan={isAdmin ? 6 : 5} className="px-3 py-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                                {markets.map(([mt, d]) => (
                                  <div key={mt} className="flex items-center justify-between text-xs">
                                    <span className="text-foreground/80 truncate pr-2">{mt}</span>
                                    <span className="flex items-center gap-2 shrink-0">
                                      <span className="text-muted-foreground font-tabular">{d.correct}/{d.total}</span>
                                      <span className={`font-bold font-tabular ${accuracyTone(d.accuracy_pct)}`}>{d.accuracy_pct.toFixed(1)}%</span>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : byLeague ? (
            <div className="text-center py-6 space-y-2" data-testid="accuracy-by-league-empty">
              <p className="text-muted-foreground text-sm">
                No leagues with ≥{byLeagueMinSamples} settled markets {byLeagueDays === 0 ? "on record" : `in the last ${byLeagueDays} days`} yet.
              </p>
              <p className="text-xs text-muted-foreground/70 max-w-xl mx-auto">
                <strong>Note:</strong> Rows here reflect our own live predictions only. NDAKUBET started grading picks on <strong>2026-06-19</strong>, so competitions with few fixtures since then will appear thin. The Top 20 Elite selector separately uses a much larger historical-base-rate corpus for its market intelligence.
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-6">Loading league accuracy…</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default AccuracyPage;


// ── Iter 204e — Asian Handicap + Goal Timing Daily 25 hit-rate tile ─────

function ValueMarketsAccuracyTile() {
  const [ah, setAh] = useState(null);
  const [gt, setGt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [a, g] = await Promise.all([
          axios.get(`${API}/asian-handicap/accuracy`),
          axios.get(`${API}/goal-timing/accuracy`),
        ]);
        if (!cancel) { setAh(a.data); setGt(g.data); }
      } catch { /* swallow */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const tone = (h) =>
    h >= 70 ? "text-success" : h >= 55 ? "text-warning" : h >= 0 ? "text-destructive" : "text-muted-foreground";

  return (
    <section className="nd-card p-6 mb-6" data-testid="value-markets-accuracy">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4" />
        <h2 className="font-display text-lg font-semibold">Value engines · Daily 25 hit-rate</h2>
        <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          self-calibrating
        </span>
      </div>
      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: "ah", label: "Asian Handicap", data: ah, href: "/asian-handicap" },
            { key: "gt", label: "Goal Timing",    data: gt, href: "/goal-timing" },
          ].map(({ key, label, data, href }) => {
            const hr = data?.hit_rate_pct;
            const total = data?.total_settled || 0;
            const tiers = data?.by_tier || {};
            return (
              <a key={key} href={href}
                 data-testid={`value-mkt-${key}`}
                 className="block rounded-xl border border-border bg-card/40 p-4 hover:bg-card/70 hover:border-foreground/30 transition-colors">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-semibold">{label}</span>
                  <span className={`font-display text-3xl font-bold tabular-nums ${tone(hr ?? -1)}`}
                        data-testid={`value-mkt-${key}-hr`}>
                    {hr != null ? `${hr}%` : "—"}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground mb-3">
                  {total} settled picks · {data?.hit ?? 0} hit · {data?.miss ?? 0} miss
                  {data?.void ? <> · {data.void} void</> : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {["A+", "A", "B+", "B", "C"].map((t) => {
                    const slot = tiers[t];
                    if (!slot || !slot.n) return null;
                    return (
                      <span key={t}
                            className="text-[10px] px-2 py-0.5 rounded border border-border bg-background/40 font-tabular"
                            data-testid={`value-mkt-${key}-tier-${t}`}>
                        <span className="font-bold">{t}</span>
                        <span className="ml-1 text-muted-foreground">
                          {slot.hit_rate_pct != null ? `${slot.hit_rate_pct}%` : "—"}
                        </span>
                        <span className="ml-1 text-muted-foreground/70">({slot.n})</span>
                      </span>
                    );
                  })}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </section>
  );
}
